import type { ElementType } from "react";
import type { MenuItem } from "@/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  alpha,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { forwardRef, useEffect, useRef, useState } from "react";

import ItemPrice from "@/components/menu/ItemPrice";
import {
  FONT_WEIGHT_BOLD,
  PAGE_PADDING,
  SHAPE,
  gutterFor,
} from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import useImageVariant from "@/hooks/useImageVariant";
import { useAppSelector } from "@/store";
import { selectPopularCatalogItems } from "@/store/selectors/catalog";

/**
 * "Popular Items" — the original's `ri` in
 * `recovery-source/live-chunks/mod-9802.js`, with its card list `n5` and the
 * card's five styled parts (`n6` frame, `n8` circular image, `n9` panel, `rn`
 * rating badge, `rt` quantity chip).
 *
 * Its data source is the selector `lm` (module 74833): featured catalog items,
 * ordered by `featured_sort_id`. The rail is gated by the tenant's
 * `web_show_featured_items` in its PARENT (`rI`), not here — this component
 * only reports whether it found anything, so the synthetic tab can appear.
 *
 * Unlike the two order rails it is NOT gated on being signed in.
 */

/* -------------------------------- styling --------------------------------- */

/** `rd` */
const Section = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    borderRadius: SHAPE.borderRadius,
    marginTop: 0,
    marginRight: PAGE_PADDING,
    marginBottom: theme.spacing(4),
    marginLeft: PAGE_PADDING,
    [theme.breakpoints.up("lg")]: { marginRight: gutter.lg, marginLeft: gutter.lg },
    [theme.breakpoints.up("xl")]: { marginRight: gutter.xl, marginLeft: gutter.xl },
    [theme.breakpoints.up("xxl")]: { marginRight: gutter.xxl, marginLeft: gutter.xxl },
    [theme.breakpoints.up("xxxl")]: {
      marginRight: gutter.xxxl,
      marginLeft: gutter.xxxl,
    },
  };
});

/** `ro` */
const HeadingRow = styled(Stack)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: PAGE_PADDING,
});

/** `rc` — the same uppercase heading the Recent Orders rail uses (`rP`). */
const Heading = styled(Typography)({
  marginBottom: "12px",
  fontWeight: "bold",
  fontSize: "20px",
  textTransform: "uppercase",
  letterSpacing: "1px",
});

/** `ra` — a plain 8px gap, NOT a `Stack spacing`. */
const ButtonRow = styled(Stack)({
  display: "flex",
  flexDirection: "row",
  gap: "8px",
});

/** `rl` — identical to `rk`/`n$`, but its icons are `medium`, not `small`. */
const ScrollButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`,
  borderRadius: "50%",
  transition: theme.transitions.create(["all"], { duration: 300 }),
  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[2],
    transform: "scale(1.05)",
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
    color: theme.palette.text.disabled,
    borderColor: theme.palette.action.disabledBackground,
  },
}));

/** `rs` — note it has no `paddingBottom`/`marginTop`, unlike the order rails. */
const Scroller = styled(Stack)({
  display: "flex",
  overflow: "hidden",
  overflowX: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

/**
 * `n6` — the card's outer frame. The circular image overhangs it on the left,
 * which is why the panel inside is right-aligned and the frame is taller than
 * the panel.
 */
const CardFrame = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== "containerHeight" && prop !== "isSoldOut",
})<{ containerHeight: number; isSoldOut?: boolean }>(
  ({ containerHeight, isSoldOut }) => ({
    height: containerHeight,
    position: "relative",
    padding: 2,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    cursor: "pointer",
    ...(isSoldOut ? { pointerEvents: "none", opacity: 0.5 } : {}),
  }),
);

/** `n8` — the circle, deliberately `contain` so a square photo is not cropped. */
const CircleImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size: number }>(({ size }) => ({
  position: "absolute",
  left: 0,
  top: 20,
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: "50%",
  objectFit: "contain",
  zIndex: Z_INDEX.BASE,
  transition: "transform 0.5s ease",
  "&:hover": { transform: "scale(1.1)" },
}));

/** `n9` */
const CardPanel = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "cardWidth" && prop !== "cardHeight",
})<{ cardWidth: number; cardHeight: number }>(
  ({ theme, cardWidth, cardHeight }) => ({
    height: `${cardHeight}px`,
    width: `${cardWidth}px`,
    alignSelf: "flex-end",
    display: "flex",
    justifyContent: "flex-end",
    boxShadow: theme.shadows[6],
    borderRadius: 12,
    padding: 12,
    position: "relative",
    overflow: "hidden",
    zIndex: Z_INDEX.NONE,
    background:
      theme.palette.mode === "light"
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`
        : undefined,
    backgroundColor: theme.palette.background.default,
  }),
);

/** `n7` — the price slot; white text so the chip reads on the tinted panel. */
const PriceSlot = styled("span")(({ theme }) => ({
  color: theme.palette.common.white,
}));

/** `re` — the Sold Out chip. */
const SoldOutChip = styled(Chip)<{ component?: ElementType }>({
  fontWeight: FONT_WEIGHT_BOLD,
  borderRadius: SHAPE.borderRadius,
  height: "18px",
  fontSize: "14px",
  padding: 2,
});

/** `rt` — the basket count, notched into the panel's top-right corner. */
const QuantityChip = styled(Chip)<{ component?: ElementType }>(({ theme }) => ({
  position: "absolute",
  right: 0,
  top: 0,
  minWidth: 14,
  height: 34,
  borderRadius: 1,
  borderBottomLeftRadius: SHAPE.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: FONT_WEIGHT_BOLD,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: theme.shadows[2],
}));

/** `rn` — the rating badge. */
const RatingBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 50,
  display: "flex",
  alignItems: "center",
  gap: 4,
  backgroundColor: alpha(theme.palette.warning.main, 0.95),
  borderRadius: 12,
  padding: "2px 8px",
  zIndex: Z_INDEX.HIGH,
}));

/** `rr` — the shining star. */
const ShiningStar = styled(StarIcon)({
  fontSize: 16,
  color: "#FFD700",
  filter: "drop-shadow(0 0 3px rgba(255, 215, 0, 0.8))",
  animation: "shine 2s ease-in-out infinite",
  "@keyframes shine": {
    "0%, 100%": {
      filter: "drop-shadow(0 0 3px rgba(255, 215, 0, 0.8))",
      transform: "scale(1)",
    },
    "50%": {
      filter: "drop-shadow(0 0 10px rgba(255, 215, 0, 1))",
      transform: "scale(1.15)",
    },
  },
});

/** `n3` */
const Name = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ isSoldOut }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  marginTop: 5,
  ...(isSoldOut ? { textDecoration: "line-through" } : {}),
}));

/* --------------------------------- the card -------------------------------- */

/** `n4` — the image is its own component purely so the variant hook has a host. */
function CardImage({ item, size }: { item: MenuItem; size: number }) {
  const { imageSrc, onImageError } = useImageVariant(item.image_url, "512x512");
  return (
    <CircleImage src={imageSrc} alt={item.name} size={size} onError={onImageError} />
  );
}

/* --------------------------------- the rail -------------------------------- */

interface PopularItemsProps {
  /** The scroll target its synthetic category tab jumps to — see `OrderAgain`. */
  id?: string;
  /** `settings.featured_category_name`; the fallback below is the original's. */
  featuredCategoryName?: string;
  quantities: Record<string, number | undefined>;
  symbol?: string;
  code?: string;
  isModernDesign?: boolean;
  onItemClick: (item: MenuItem) => void;
  /** Reports "there is at least one featured item" upward. Nothing else. */
  setHasPopularItems?: (value: boolean) => void;
}

/** `ri` */
const PopularItems = forwardRef<HTMLDivElement, PopularItemsProps>(
  function PopularItems(
    {
      id,
      featuredCategoryName,
      quantities,
      symbol,
      code,
      isModernDesign,
      onItemClick,
      setHasPopularItems,
    },
    ref,
  ) {
    const items = useAppSelector(selectPopularCatalogItems);
    const theme = useTheme();
    // `n5`'s own two queries. They are DOWN queries, so they read
    // "narrower than sm/md", and the four sizes step with them.
    const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
    const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
    const imageSize = isBelowSm ? 80 : isBelowMd ? 100 : 130;
    const cardWidth = isBelowSm ? 200 : isBelowMd ? 250 : 300;
    const cardHeight = isBelowSm ? 130 : 150;
    const containerHeight = isBelowSm ? 200 : 250;

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    useEffect(() => {
      setHasPopularItems?.(items.length > 0);
    }, [setHasPopularItems, items.length]);

    // `n5`'s check, with its own 10px slack on the right (the order rails use 1px).
    useEffect(() => {
      const element = scrollRef.current;
      if (!element) return undefined;
      const update = () => {
        const { scrollLeft, scrollWidth, clientWidth } = element;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      };
      update();
      element.addEventListener("scroll", update);
      window.addEventListener("resize", update);
      return () => {
        element.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      };
    }, [items.length]);

    const scroll = (direction: "left" | "right") => {
      scrollRef.current?.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    };

    if (items.length === 0) return null;

    return (
      <Section ref={ref} id={id} isModernDesign={isModernDesign}>
        <HeadingRow>
          <Heading variant="h5">
            {featuredCategoryName || "Popular Items"} 🔥
          </Heading>
          <ButtonRow>
            <ScrollButton
              size="small"
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              aria-label="scroll left"
            >
              <ArrowBackIcon fontSize="medium" />
            </ScrollButton>
            <ScrollButton
              size="small"
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              aria-label="scroll right"
            >
              <ArrowForwardIcon fontSize="medium" />
            </ScrollButton>
          </ButtonRow>
        </HeadingRow>
        <Scroller ref={scrollRef} direction="row" spacing={2}>
          {items.map((item) => (
            <CardFrame
              key={item._id}
              containerHeight={containerHeight}
              isSoldOut={item.sold_out}
              onClick={() => onItemClick(item)}
            >
              <CardImage item={item} size={imageSize} />
              <CardPanel cardWidth={cardWidth} cardHeight={cardHeight}>
                <PriceSlot>
                  {item.sold_out ? (
                    <SoldOutChip
                      color="error"
                      component="span"
                      label="Sold Out"
                      size="small"
                    />
                  ) : (
                    <ItemPrice item={item} symbol={symbol} code={code} color="secondary" />
                  )}
                </PriceSlot>
                {/* The "5" is a LITERAL in the original — this badge is not
                    wired to any rating field and shows the same number on
                    every card. Reproduced, not invented. */}
                <RatingBadge>
                  <ShiningStar />
                  <Typography variant="caption" fontWeight="bold" color="white">
                    5
                  </Typography>
                </RatingBadge>
                {(quantities[item._id] ?? 0) > 0 ? (
                  <QuantityChip
                    component="span"
                    label={String(quantities[item._id])}
                  />
                ) : (
                  <ShoppingCartIcon
                    fontSize="medium"
                    color="primary"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  />
                )}
                <Name
                  color="text.primary"
                  variant="subtitle2"
                  fontWeight="bold"
                  isSoldOut={item.sold_out}
                >
                  {item.name}
                </Name>
              </CardPanel>
            </CardFrame>
          ))}
        </Scroller>
      </Section>
    );
  },
);

export default PopularItems;
